/**
 * ProfitYo Reconciliation Forensic Engine
 * Handles complex balancing logic and auto-matching heuristics.
 */
import { differenceInDays, parseISO } from 'date-fns';

class ReconciliationEngine {
  /**
   * Calculates the Cleared Balance based on GAAP rules
   * For Banks (Assets): Start + Deposits - Payments
   * For Credit Cards (Liabilities): Start - Payments + Charges
   */
  calculateClearedBalance(startBalance, selectedTransactions, accountType = 'assets') {
    return selectedTransactions.reduce((total, tx) => {
      const amount = Math.abs(tx.amount);
      if (accountType === 'assets') {
        return tx.amount > 0 ? total + amount : total - amount;
      } else {
        // Liabilities: Charges increase balance, payments decrease it
        return tx.amount < 0 ? total + amount : total - amount;
      }
    }, startBalance);
  }

  /**
   * Auto-matching Heuristics
   * Compares Bank Statement lines with Book Records
   */
  suggestMatches(bookTransactions, bankStatementLines) {
    return bookTransactions.map(tx => {
      const potentialMatches = bankStatementLines.filter(line => 
        Math.abs(line.amount) === Math.abs(tx.amount)
      );

      if (potentialMatches.length === 0) return { ...tx, matchScore: 0 };

      // Calculate confidence based on date proximity and payee name
      const bestMatch = potentialMatches.map(match => {
        let score = 50; // Base score for amount match
        
        // Date proximity (within 3 days = high confidence)
        const dateDiff = Math.abs(differenceInDays(parseISO(tx.date), parseISO(match.date)));
        if (dateDiff === 0) score += 30;
        else if (dateDiff <= 3) score += 15;

        // Payee similarity
        if (tx.description.toLowerCase().includes(match.description.toLowerCase()) || 
            match.description.toLowerCase().includes(tx.description.toLowerCase())) {
          score += 18;
        }

        return { ...match, score };
      }).sort((a, b) => b.score - a.score)[0];

      return {
        ...tx,
        suggestedMatch: bestMatch,
        matchScore: bestMatch.score
      };
    });
  }
}

export const reconEngine = new ReconciliationEngine();